/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, UserDTO, UserGroupDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface IUserGroupMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const UserGroupMaintenance: React.FC<IUserGroupMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [userGroupDTO, setUserGroupDTO] = useState<UserGroupDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setUserGroupDTO({
            ...userGroupDTO,
            id: '',
            name: '',
            description: '',
            companyId: userInfo.user.companyId,
        } as UserGroupDTO);

        if(isOpenMaintenance){
            handleUserGetAllAsync();
            handleGetUserGroupByIdAsync();
            handleGetPermission(ListId.authsphere.userGroupId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userGroupFilterDTO = {
            companyId: '',
        };
        GenericService.GetAllAsync1('authsphere.user', userGroupFilterDTO, setLoading, setUserList, showMessage);
    }, []);

    const handleGetUserGroupByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('authsphere.userGroup', data.id, setLoading, setUserGroupDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = userGroupDTO as UserGroupDTO;

        if (userGroupDTO?.id === '' || userGroupDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('authsphere.userGroup', dto, setUserGroupDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage(t('userGroup.maintenance.message.accessRolePermissionDTO'), 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('authsphere.userGroup', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [userGroupDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: t('userGroup.maintenance.dialogActions.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick }, // save
        { id: 'cancel', text: t('userGroup.maintenance.dialogActions.cancel'), icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined }, // cancel
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('userGroup.maintenance.dialogForm.title')}
                icon='manage_accounts_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='name'
                                label={t('userGroup.maintenance.textFieldInput.name')}
                                value={userGroupDTO?.name}
                                setValue={setUserGroupDTO}
                                obj={userGroupDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        {/* <Grid item xs={12} md={4}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                placeholder='Company'
                                value={userGroupDTO?.companyId}
                                setValue={setUserGroupDTO}
                                obj={userGroupDTO}
                                valueList={companyList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid> */}
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label={t('userGroup.maintenance.textFieldInput.description')}
                                value={userGroupDTO?.description}
                                setValue={setUserGroupDTO}
                                obj={userGroupDTO}
                                required={false}
                                maxLength={400}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {userGroupDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={userGroupDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('userGroup.maintenance.textFieldReadOnly.createdById')}
                                        value={userList.find(x => x.id === userGroupDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('userGroup.maintenance.textFieldReadOnly.creationDateTime')}
                                        value={formatDateTime(String(userGroupDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('userGroup.maintenance.textFieldReadOnly.updatedById')}
                                        value={userList.find(x => x.id === userGroupDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('userGroup.maintenance.textFieldReadOnly.updateDateTime')}
                                        value={formatDateTime(String(userGroupDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};
